import { useEffect } from "react";

import { useAttachments } from "features/attachments";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import { areArraysEqual } from "features/templates/utils";

import useWorkspaceAttachmentsMutations from "common/datahooks/useWorkspaceAttachmentsMutations";

import type { UseFormReturn } from "react-hook-form";

export default function useTemplateEditorAttachments({
  form: { setValue, getValues, subscribe },
}: {
  form: UseFormReturn<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>;
}) {
  const { uploadAttachment } = useWorkspaceAttachmentsMutations();

  const initialData = getValues();

  const {
    attachments,
    onUpload,
    onUploadCancel,
    removeAttachment,
    resetAttachments,
  } = useAttachments(
    "attachments" in initialData
      ? initialData.attachments.map(
          ({ id, file_name, file_metadata: { mimetype, size } }) => ({
            id,
            mimeType: mimetype,
            name: file_name,
            byteSize: size,
          }),
        )
      : [],
    async (file, abortSignal) => {
      const uploadedAttachment = await uploadAttachment({ file, abortSignal });

      const existingAttachments = getValues("attachments");

      setValue(
        "attachments",
        existingAttachments
          ? [...existingAttachments, uploadedAttachment]
          : [uploadedAttachment],
        { shouldDirty: true },
      );

      const {
        id,
        url,
        file_name,
        file_metadata: { size, mimetype },
      } = uploadedAttachment;

      return {
        id,
        name: file_name,
        byteSize: size,
        mimeType: mimetype,
        url,
      };
    },
  );

  function onRemoveAttachment(attachmentId: string) {
    setValue(
      "attachments",
      getValues("attachments").filter(({ id }) => id !== attachmentId),
      { shouldDirty: true },
    );
    removeAttachment(attachmentId);
  }

  useEffect(() => {
    return subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        if (values.type === "NOTE_TEMPLATE") {
          resetAttachments();
        } else if (
          attachments.every((attachment) => "id" in attachment) &&
          !areArraysEqual(attachments, values.attachments, (item1, item2) =>
            "id" in item1 ? item1.id === item2.id : false,
          )
        ) {
          resetAttachments(
            values.attachments.map(
              ({ id, file_name, file_metadata: { mimetype, size } }) => ({
                id,
                mimeType: mimetype,
                name: file_name,
                byteSize: size,
              }),
            ),
          );
        }
      },
    });
  }, [subscribe, attachments]);

  return {
    attachments,
    onUpload,
    onUploadCancel,
    onRemoveAttachment,
  };
}
